import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { Paginated, RestrictedPerson } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Pessoas restritas — a lista de bloqueio de acesso ao parque.
 *
 * É uma lista de segurança, não um cadastro: CPF, nome e o MOTIVO (obrigatório,
 * fica na auditoria). A portaria consulta por CPF na entrada.
 */
export function RestritosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<RestrictedPerson | null>(null);

  const listQuery = useQuery({
    queryKey: ['restricted-people', { search, page }],
    queryFn: () =>
      api.get<Paginated<RestrictedPerson>>('/restricted-people', {
        search: search || undefined,
        page,
        perPage: 25,
      }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['restricted-people'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/restricted-people/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const columns: Array<Column<RestrictedPerson>> = [
    {
      key: 'document',
      label: 'CPF',
      mono: true,
      strong: true,
      render: (row) => formatDocument(row.document),
    },
    { key: 'name', label: 'Nome', strong: true, render: (row) => row.name },
    { key: 'reason', label: 'Motivo do bloqueio', render: (row) => blank(row.reason) },
    { key: 'createdAt', label: 'Bloqueado em', mono: true, render: (row) => formatDate(row.createdAt) },
    {
      key: 'actions',
      label: '',
      width: 56,
      right: true,
      render: (row) =>
        can('restritos:excluir') ? (
          <Button
            small
            variant="ghost"
            icon={Trash2}
            aria-label={`Desbloquear ${row.name}`}
            onClick={() => setRemoving(row)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Pessoas restritas"
        actions={
          can('restritos:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Bloquear pessoa
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por CPF ou nome…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
        />

        {listQuery.isPending && <TableSkeleton columns={5} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Pessoas com acesso bloqueado"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            onPageChange={setPage}
            emptyState={
              search ? (
                <NoResultsState onClear={() => setSearch('')} />
              ) : (
                <EmptyState
                  title="Nenhuma pessoa restrita"
                  description="Bloqueios de acesso aparecem aqui e valem na portaria e na venda."
                />
              )
            }
          />
        )}
      </div>

      <RestrictModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title={removing ? `Desbloquear ${removing.name}?` : ''}
        confirmLabel="Desbloquear"
        message={
          <>
            A pessoa volta a poder entrar no parque. O bloqueio original continua
            registrado na auditoria.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

function RestrictModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [document, setDocument] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDocument('');
      setName('');
      setReason('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/restricted-people', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);
  const canSubmit = document.replace(/\D/g, '').length >= 11 && name.trim().length >= 2 && reason.trim().length >= 5;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bloquear pessoa"
      description="O bloqueio vale na portaria e impede novas vendas para o CPF"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() =>
              mutation.mutate({ document, name: name.trim(), reason: reason.trim() })
            }
          >
            Bloquear
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <div className="grid-2">
          <Field label="CPF" htmlFor="restrict-cpf" error={errors.document}>
            <input
              id="restrict-cpf"
              className="input input--mono"
              inputMode="numeric"
              value={document}
              onChange={(event) => setDocument(event.target.value)}
              placeholder="000.000.000-00"
            />
          </Field>
          <Field label="Nome" htmlFor="restrict-name" error={errors.name}>
            <input
              id="restrict-name"
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Motivo do bloqueio"
          htmlFor="restrict-reason"
          error={errors.reason}
          hint="Mínimo de 5 caracteres — fica na ficha e na auditoria"
        >
          <textarea
            id="restrict-reason"
            className="input"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
